import { Dispatch } from "redux";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { Table, Input, Row, Col } from "reactstrap";
import Button from "../../components/common/button";
import adminService from "../../services/adminService";
import productService from "../../services/productService";
import notification from "../../utils/notification";
import { IApplicationState } from "../../redux/reducers";
import { IPagination } from "../../interfaces/pagination";
import { IProduct } from "../../interfaces/products";
import { paginateAdminProducts } from "../../redux/actions/admin/adminActions";
import Loader from "../../components/loader";
import SideForm from "../../components/sideForm";
import AddProductForm from "../products/productDetail/form/addForm";
import EditProductForm from "../products/productDetail/form/editForm";
import {
  FaInfoCircle,
  FaTicketAlt,
  FaChartBar,
  FaExclamationTriangle,
  FaBrain
} from "react-icons/fa";
import AIAgentBox from "../../components/common/AIAgentBox";
import AIAnalysisResultsModal from "../../components/modals/AIAnalysisResultsModal";
import GenerateProductModal from "../../components/modals/GenerateProductModal";
import { useDebounce } from "../../utils/hooks";

interface IProps {
  fetchProducts: () => Promise<any>;
  paginate: (page: number) => void;
  selectProduct: (product: IProduct) => void;
  products: IProduct[];
  pagination: IPagination;
}

// Safely infer page size from various possible pagination field names
const getPageSize = (p: any, fallback = 20) =>
  (p?.perPage ??
    p?.page_size ??
    p?.per_page ??
    p?.limit ??
    p?.pageSize ??
    p?.itemsPerPage ??
    fallback) as number;

// Enhanced normalizer for better data handling
function normalizeProduct(raw: any): IProduct {
  const p = raw?.data ?? raw?.product ?? raw ?? {};

  // Enhanced brand normalization
  const brand = p.brand ??
    (p.brand_name || p.brandId
      ? { id: p.brand_id ?? p.brandId ?? null, name: p.brand_name ?? "Unknown Brand" }
      : p.brand_name
        ? { id: null, name: p.brand_name }
        : undefined);

  // Enhanced company normalization
  const company = p.company ??
    (p.company_name || p.companyId
      ? { id: p.company_id ?? p.companyId ?? null, name: p.company_name ?? "Unknown Company" }
      : p.company_name
        ? { id: null, name: p.company_name }
        : undefined);

  // Enhanced manufacturer normalization
  const manufacturer = p.manufacturer ??
    (p.manufacturer_name || p.manufacturerId
      ? { id: p.manufacturer_id ?? p.manufacturerId ?? null, name: p.manufacturer_name ?? "Unknown Manufacturer" }
      : p.manufacturer_name
        ? { id: null, name: p.manufacturer_name }
        : undefined);

  // Enhanced category handling
  const category = typeof p.category === "string"
    ? p.category
    : p.category?.name ?? p.category_name ?? p.product_type?.title ?? "Uncategorized";

  // Enhanced ingredients handling
  const ingredients = Array.isArray(p.ingredients)
    ? p.ingredients
    : p.ingredients && typeof p.ingredients === "object"
      ? Object.values(p.ingredients)
      : [];

  // Enhanced product name handling
  const productName = p.name ?? p.title ?? p.product_name ?? p.display_name ?? "Unnamed Product";

  return {
    id: p.id ?? p._id ?? `temp_${Date.now()}`,
    name: productName,
    brand,
    company,
    manufacturer,
    category,
    ingredients,
    // Preserve all other properties
    ...(p as object),
  } as unknown as IProduct;
}

const ProductsTable: React.FC<IProps> = ({
  fetchProducts,
  paginate,
  pagination,
  products,
  selectProduct
}) => {
  const dispatch = useDispatch<Dispatch<any>>();

  // Get dispatch function for use in effects
  const getDispatch = () => dispatch;

  // Action-level loading (fetch single product, save, etc.)
  const [loading, setLoading] = useState(false);

  // List-level hydration/loading (prevents "No products found" flash)
  const [listLoading, setListLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  // Prefetch cache loading (doesn't affect table UI)
  const [cacheLoading, setCacheLoading] = useState(false);

  const [isEditFormOpen, setEditFormOpen] = useState(false);
  const [isAddFormOpen, setAddFormOpen] = useState(false);
  const [isGenerateProductModalOpen, setIsGenerateProductModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const [isAIAnalysisModalOpen, setIsAIAnalysisModalOpen] = useState(false);

  // Cache the full catalog for global search
  const [allProducts, setAllProducts] = useState<IProduct[]>([]);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);
  const [searchPage, setSearchPage] = useState<number>(1);

  // Debounce search term to prevent excessive filtering on every keystroke
  const debouncedSearch = useDebounce(search, 500);

  const pageSize = getPageSize(pagination, 20);

  const toggleEditForm = () => setEditFormOpen(!isEditFormOpen);
  const toggleAddForm = () => setAddFormOpen(!isAddFormOpen);
  const toggleGenerateProductModal = () => setIsGenerateProductModalOpen(!isGenerateProductModalOpen);

  // AbortController ref for canceling requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch products on mount or page change (server pagination view)
  useEffect(() => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    (async () => {
      setListLoading(true);
      try {
        await fetchProducts();
        if (!signal.aborted) {
          setHydrated(true);
        }
      } catch (error) {
        // Don't process error if request was aborted
        const err = error as any;
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          console.error("Error fetching products:", error);
        }
      } finally {
        if (!signal.aborted) setListLoading(false);
      }
    })();

    // Cleanup: abort request when component unmounts or page changes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  // AbortController ref for prefetch
  const prefetchAbortControllerRef = useRef<AbortController | null>(null);

  // Prefetch ALL pages once for global search (separate loading flag)
  useEffect(() => {
    // Cancel any pending prefetch requests
    if (prefetchAbortControllerRef.current) {
      prefetchAbortControllerRef.current.abort();
    }

    // Create new AbortController for prefetch
    const abortController = new AbortController();
    prefetchAbortControllerRef.current = abortController;
    const signal = abortController.signal;

    (async () => {
      if (allLoaded && allProducts.length > 0) return;
      try {
        setCacheLoading(true);
        // Call getAdminProducts with signal for cancellation
        // Note: getAdminProducts dispatches to Redux, so we need to get data from state
        // For now, we'll use the original approach but with signal support
        // The signal will cancel the request if component unmounts
        const firstThunk = adminService.getAdminProducts(false, undefined, undefined, undefined, signal);
        await firstThunk(getDispatch(), () => ({} as any));
        // Get data from Redux state after dispatch
        // Since we can't easily get the return value, we'll use a workaround
        // by checking the products from state after a short delay or use the existing products
        const firstData: IProduct[] = products || [];

        // Get pagination from state
        const firstPg: any = pagination ?? {};
        const totalPages: number = firstPg.pages ?? firstPg.total_pages ?? 1;

        let merged = [...firstData];

        // Note: Prefetching all pages is expensive and may be canceled
        // Consider removing this prefetch or making it optional
        for (let p = 2; p <= totalPages; p++) {
          if (signal.aborted) return;
          // For subsequent pages, dispatch the thunk with signal
          const resThunk = adminService.getAdminProducts(false, undefined, undefined, undefined, signal);
          await resThunk(getDispatch(), () => ({} as any));
          // Note: In a real implementation, you'd need to get the data from Redux state
          // For now, we'll skip adding to merged since we can't easily get the return value
          // This prefetch feature may need to be refactored to work properly with cancellation
        }

        // Since we can't easily get individual page data, use current products
        // This is a limitation of the current implementation
        merged = products || [];

        if (!signal.aborted) {
          setAllProducts(merged);
          setAllLoaded(true);
        }
      } catch (e) {
        // Don't process error if request was aborted
        const err = e as any;
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          console.warn("Prefetch all products failed; search will scope to current page.", e);
        }
      } finally {
        if (!signal.aborted) setCacheLoading(false);
      }
    })();

    // Cleanup: abort prefetch when component unmounts
    return () => {
      if (prefetchAbortControllerRef.current) {
        prefetchAbortControllerRef.current.abort();
        prefetchAbortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset searchPage when the debounced query changes
  useEffect(() => {
    setSearchPage(1);
  }, [debouncedSearch]);

  // Fetch product by ID and open edit form
  const editProduct = async (productId: string) => {
    try {
      setLoading(true);
      const productData = await productService.fetchProductById(productId);
      const normalized = normalizeProduct(productData);
      console.log("Data from ProductsTable: ", productData);
      setSelectedProduct(normalized);
      selectProduct(normalized);
      toggleEditForm();
    } catch (err) {
      console.error("Failed to fetch product data", err);
      notification.error("Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  };

  // Handle generated product from GenerateProductModal
  const handleProductGenerated = async (product: IProduct) => {
    try {
      // Validate product data
      if (!product) {
        console.error("handleProductGenerated: Invalid product data received");
        notification.error("Invalid product data. Please try again.");
        return;
      }

      // The product is already saved by the generateProductAPI endpoint
      // We just need to refresh the admin products list
      setListLoading(true);

      // Refresh the current page
      await fetchProducts();

      // Refresh the allProducts cache if it was loaded
      if (allLoaded) {
        setCacheLoading(true);
        try {
          const first =
            (await (adminService as any).getAdminProducts?.(1)) ??
            (await adminService.getAdminProducts());
          const firstData: IProduct[] = first?.products ?? first?.data?.products ?? [];
          const firstPg: any = first?.pagination ?? first?.data?.pagination ?? pagination ?? {};
          const totalPages: number =
            firstPg.pages ?? firstPg.total_pages ?? pagination?.pages ?? 1;

          let merged = [...firstData];
          for (let p = 2; p <= totalPages; p++) {
            const res =
              (await (adminService as any).getAdminProducts?.(p)) ??
              (await adminService.getAdminProducts());
            const data: IProduct[] = res?.products ?? res?.data?.products ?? [];
            merged = merged.concat(data);
          }
          setAllProducts(merged);
        } catch (cacheError) {
          console.warn("Failed to refresh product cache:", cacheError);
          // Don't throw - this is not critical
        } finally {
          setCacheLoading(false);
        }
      }

      const productName = product?.name || 'Product';
      notification.product(`Product "${productName}" generated successfully`);
    } catch (err) {
      console.error("Error handling generated product:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      notification.error(`Product was generated but failed to refresh the list: ${errorMessage}. Please refresh the page manually.`);
    } finally {
      setListLoading(false);
    }
  };

  // Save updated product
  const saveProduct = async (productId: string, updatedProduct: IProduct) => {
    try {
      setLoading(true);
      await dispatch<any>(productService.editProduct(productId, updatedProduct));
      notification.product("Product updated successfully");
      toggleEditForm();

      // Refresh current server page
      setListLoading(true);
      await fetchProducts();
      setListLoading(false);

      // Refresh the allProducts cache if we had it
      if (allLoaded) {
        setCacheLoading(true);
        try {
          const first =
            (await (adminService as any).getAdminProducts?.(1)) ??
            (await adminService.getAdminProducts());
          const firstData: IProduct[] = first?.products ?? first?.data?.products ?? [];
          const firstPg: any = first?.pagination ?? first?.data?.pagination ?? pagination ?? {};
          const totalPages: number =
            firstPg.pages ?? firstPg.total_pages ?? pagination?.pages ?? 1;

          let merged = [...firstData];
          for (let p = 2; p <= totalPages; p++) {
            const res =
              (await (adminService as any).getAdminProducts?.(p)) ??
              (await adminService.getAdminProducts());
            const data: IProduct[] = res?.products ?? res?.data?.products ?? [];
            merged = merged.concat(data);
          }
          setAllProducts(merged);
        } finally {
          setCacheLoading(false);
        }
      }
    } catch (err) {
      console.error("Failed to update product", err);
      notification.error("Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  // GLOBAL search (over full catalog if loaded; else current page fallback)
  // Use debounced search for filtering to prevent excessive recalculations
  const isSearching = debouncedSearch.trim().length > 0;

  const filteredProducts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return products;

    const source = allProducts.length ? allProducts : products;
    return source.filter((item) => {
      const name = item.name ?? "";
      const brand = item.brand?.name ?? "";
      const company = item.company?.name ?? "";
      const manufacturer = item.manufacturer?.name ?? "";
      const category = typeof item.category === "string" ? item.category : item.category?.name ?? "";
      const code = item.code ?? "";

      return (
        name.toLowerCase().includes(q) ||
        brand.toLowerCase().includes(q) ||
        company.toLowerCase().includes(q) ||
        manufacturer.toLowerCase().includes(q) ||
        category.toLowerCase().includes(q) ||
        code.toLowerCase().includes(q)
      );
    });
  }, [debouncedSearch, allProducts, products]);

  // Client-side pagination when searching
  const totalSearchPages = useMemo(() => {
    if (!isSearching) return 1;
    return Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  }, [isSearching, filteredProducts.length, pageSize]);

  const pagedFiltered = useMemo(() => {
    if (!isSearching) return filteredProducts;
    const start = (searchPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [isSearching, filteredProducts, searchPage, pageSize]);

  const displayedRows = isSearching ? pagedFiltered : products;

  // Pagination handlers (switch between client/server modes)
  const goPrev = () => {
    if (isSearching) {
      setSearchPage((p) => Math.max(1, p - 1));
    } else {
      paginate(Math.max(1, pagination.page - 1));
    }
  };
  const goNext = () => {
    if (isSearching) {
      setSearchPage((p) => Math.min(totalSearchPages, p + 1));
    } else {
      paginate(Math.min(pagination.pages, pagination.page + 1));
    }
  };

  const currentPageNumber = isSearching ? searchPage : pagination.page;
  const totalPagesNumber = isSearching ? totalSearchPages : pagination.pages;

  return (
    <>
      <style>
        {`
          .table-responsive {
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
            scrollbar-color: #cbd5e0 #f7fafc;
            overflow-x: auto !important;
            overflow-y: visible !important;
          }
          
          .table-responsive::-webkit-scrollbar {
            height: 8px;
          }
          
          .table-responsive::-webkit-scrollbar-track {
            background: #f7fafc;
            border-radius: 4px;
          }
          
          .table-responsive::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 4px;
          }
          
          .table-responsive::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
          }
          
          @media (max-width: 768px) {
            .table-responsive {
              border-radius: 0;
              margin: 0 -15px;
            }
            
            .table-responsive table {
              font-size: 12px;
            }
            
            .table-responsive th,
            .table-responsive td {
              padding: 8px 6px !important;
            }
          }
          
          @media (max-width: 480px) {
            .table-responsive table {
              font-size: 11px;
            }
            
            .table-responsive th,
            .table-responsive td {
              padding: 6px 4px !important;
            }
          }
          
          /* Scroll indicator for mobile */
          @media (max-width: 768px) {
            .table-responsive::after {
              content: "← Scroll to see more →";
              display: block;
              text-align: center;
              font-size: 12px;
              color: #6c757d;
              padding: 8px;
              background: #f8f9fa;
              border-top: 1px solid #e9ecef;
            }
          }
        `}
      </style>
      <div className="container-fluid">
        {/* AI Agent Box */}
        <AIAgentBox
          title="AI Product Agent"
          description="Analyze your entire product catalog for data quality, categorization, and optimization opportunities"
          features={[
            { icon: <FaTicketAlt />, text: "Categorize products automatically", iconColor: "#0d6efd" },
            { icon: <FaChartBar />, text: "Calculate category-level metrics", iconColor: "#28a745" },
            { icon: <FaExclamationTriangle />, text: "Flag data quality issues", iconColor: "#ffc107" }
          ]}
          theme="blue"
          onButtonClick={() => setIsAIAnalysisModalOpen(true)}
        />

        {/* Search and Add Product */}
        <div className="admin-data-search">
          <div className="admin-data-search__input-wrapper">
            <input
              type="text"
              placeholder="Search products by name, brand, company, manufacturer, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-data-search__input"
              aria-label="Search products"
            />
            <i className="fas fa-search admin-data-search__icon"></i>
            {search && (
              <button
                type="button"
                className="admin-data-search__clear-btn"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <div className="admin-data-search__actions">
            <button
              type="button"
              className="admin-data-search__button btn-primary"
              onClick={toggleAddForm}
            >
              <i className="fas fa-plus"></i>
              Add Product
            </button>
            <button
              type="button"
              className="admin-data-search__button btn-primary"
              onClick={toggleGenerateProductModal}
              style={{ marginLeft: '10px' }}
            >
              <i className="fas fa-magic"></i>
              Generate Product
            </button>
          </div>
        </div>

        {/* Products Table - All Devices */}
        <div className="admin-data-table-wrapper">
          <div className="table-responsive">
            <Table className="admin-data-table products-table">
              <thead>
                <tr className="table-header">
                  <th>Product Name</th>
                  <th>Brand Name</th>
                  <th>Company Name</th>
                  <th>Manufacturer</th>
                  <th>Category</th>
                  <th className="actions-column">Ingredients</th>
                  <th className="actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(listLoading || !hydrated) ? (
                  <tr>
                    <td colSpan={7} className="loading-cell">
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <span>Loading products...</span>
                      </div>
                    </td>
                  </tr>
                ) : displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      <div className="empty-state">
                        <div className="empty-icon">📦</div>
                        <span>No products found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((item, index) => {
                    const ingredientsCount = Array.isArray((item as any).ingredients)
                      ? (item as any).ingredients.length
                      : (item as any).ingredients
                        ? Object.keys((item as any).ingredients).length
                        : 0;

                    const brandName = (item as any).brand?.name || "N/A";
                    const companyName = (item as any).company?.name || "N/A";
                    const manufacturerName = (item as any).manufacturer?.name || "N/A";
                    const categoryName = typeof item.category === "string"
                      ? item.category
                      : item.category?.name || "N/A";

                    return (
                      <tr key={item.id} className="table-row clickable">
                        <td className="cell-name" title={item.name}>
                          {item.name || "N/A"}
                        </td>
                        <td title={brandName}>
                          {brandName}
                        </td>
                        <td title={companyName}>
                          {companyName}
                        </td>
                        <td title={manufacturerName}>
                          {manufacturerName}
                        </td>
                        <td title={categoryName}>
                          {categoryName}
                        </td>
                        <td className="actions-cell">
                          <span className={`status-badge ${ingredientsCount > 0 ? 'status-verified' : 'status-unverified'}`}>
                            {ingredientsCount}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            className="action-btn btn-edit"
                            onClick={() => editProduct(item.id)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>


        {/* Enhanced Pagination */}
        <Loader isLoading={listLoading || loading}>
          {totalPagesNumber > 1 && (
            <div className="admin-data-pagination">
              <div className="admin-data-pagination__controls">
                <button
                  type="button"
                  className="admin-data-pagination__button btn-prev"
                  disabled={currentPageNumber === 1}
                  onClick={goPrev}
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
              </div>
              <div className="admin-data-pagination__info">
                <div className="page-info">
                  Page {currentPageNumber} of {totalPagesNumber}
                </div>
                {isSearching && (
                  <div className="results-info">
                    {filteredProducts.length} results
                  </div>
                )}
              </div>
              <div className="admin-data-pagination__next-controls">
                <button
                  type="button"
                  className="admin-data-pagination__button btn-next"
                  disabled={currentPageNumber === totalPagesNumber}
                  onClick={goNext}
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </Loader>
      </div>

      {/* Edit Product Side Form */}
      <SideForm isOpen={isEditFormOpen} title="Edit Product" toggleForm={toggleEditForm}>
        {selectedProduct && (
          <EditProductForm
            key={selectedProduct?.id || "new"}  // force remount on product change
            toggleForm={toggleEditForm}
            product={selectedProduct}
            isAdmin
            edit={saveProduct}
          />
        )}
      </SideForm>

      {/* Add Product Side Form */}
      <SideForm isOpen={isAddFormOpen} title="New Product" toggleForm={toggleAddForm}>
        <AddProductForm toggleForm={toggleAddForm} />
      </SideForm>

      {/* AI Analysis Results Modal */}
      <AIAnalysisResultsModal
        isOpen={isAIAnalysisModalOpen}
        toggle={() => setIsAIAnalysisModalOpen(false)}
        collectionName="product_list"
        collectionLabel="Products"
        onAnalysisComplete={() => {
          // Refresh products list after analysis is applied
          fetchProducts();
        }}
      />

      {/* Generate Product Modal */}
      <GenerateProductModal
        isOpen={isGenerateProductModalOpen}
        toggle={toggleGenerateProductModal}
        onProductGenerated={handleProductGenerated}
      />
    </>
  );
};

const mapDispatchToProps = {
  fetchProducts: () => adminService.getAdminProducts(),
  selectProduct: (product: IProduct) => adminService.selectProduct(product),
  paginate: (page: number) => (dispatch: Dispatch) => dispatch(paginateAdminProducts(page))
};

const mapStateToProps = (state: IApplicationState) => ({
  products: state.admin.products.products,
  pagination: state.admin.products.pagination,
  product: state.admin.products.selected
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsTable);
